import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  EditIcon,
  MailIcon,
  PhoneIcon,
  PowerIcon,
  UserCogIcon,
  UserIcon,
  LandmarkIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import {
  EmploymentTypeBadge,
  InfoField,
  InstructorAvatar,
  InstructorStatusBadge,
  MonthlyFreeQuotaBadge,
  PayoutRateGrid,
} from "../ui/instructors/instructor-display";
import {
  useInstructorCheckInsQuery,
  useInstructorClassSessionsQuery,
  useInstructorPrivateLessonsQuery,
  useInstructorQuery,
  useInstructorSummaryQuery,
} from "../api/queries";
import { InstructorCheckInsTable } from "../ui/InstructorCheckInsTable";
import { InstructorClassSessionsTable } from "../ui/InstructorClassSessionsTable";
import { InstructorPrivateLessonsTable } from "../ui/InstructorPrivateLessonsTable";
import { InstructorSummaryCards } from "../ui/InstructorSummaryCards";

function InstructorProfilePage() {
  const nav = useNavigate({ from: "/app/master/instructors/$id" });
  const { id } = useParams({ from: "/app/master/instructors/$id" });
  const search = useSearch({ from: "/app/master/instructors/$id" }) as {
    tab?: "general" | "class-sessions" | "private-lessons" | "check-ins";
  };
  const { data: instructor, ...result } = useInstructorQuery(id);
  const { data: summary } = useInstructorSummaryQuery(id);
  const activeTab = search.tab ?? "general";

  // ຊົ່ວໂມງຮຽນຄລາດສ໌ state
  const [classSessionsOffset, setClassSessionsOffset] = useState(0);
  const [classSessionsLimit] = useState(20);
  const [classSessionsSort, setClassSessionsSort] = useState<
    { field: string; dir: "asc" | "desc" }[] | undefined
  >(undefined);

  const classSessionsQuery = useInstructorClassSessionsQuery(id, {
    offset: classSessionsOffset,
    limit: classSessionsLimit,
    sort: classSessionsSort,
    filters: undefined,
  });

  // Private Lessons state
  const [privateLessonsOffset, setPrivateLessonsOffset] = useState(0);
  const [privateLessonsLimit] = useState(20);
  const [privateLessonsSort, setPrivateLessonsSort] = useState<
    { field: string; dir: "asc" | "desc" }[] | undefined
  >(undefined);

  const privateLessonsQuery = useInstructorPrivateLessonsQuery(id, {
    offset: privateLessonsOffset,
    limit: privateLessonsLimit,
    sort: privateLessonsSort,
    filters: undefined,
  });

  // Check-ins state
  const [checkInsOffset, setCheckInsOffset] = useState(0);
  const [checkInsLimit] = useState(20);
  const [checkInsSort, setCheckInsSort] = useState<
    { field: string; dir: "asc" | "desc" }[] | undefined
  >(undefined);

  const checkInsQuery = useInstructorCheckInsQuery(id, {
    offset: checkInsOffset,
    limit: checkInsLimit,
    sort: checkInsSort,
    filters: undefined,
  });

  const canEdit = useActionPermission(["master:update"]);

  // Memoized sorting handlers to prevent infinite loops
  const handleClassSessionsSort = useCallback(
    (id: string, desc: boolean) => {
      const dir: "asc" | "desc" = desc ? "desc" : "asc";
      const newSort: Array<{ field: string; dir: "asc" | "desc" }> = [
        { field: id, dir },
      ];
      const currentField = classSessionsSort?.[0]?.field;
      const currentDir = classSessionsSort?.[0]?.dir;
      if (currentField === id && currentDir === dir) {
        return; // No change, prevent infinite loop
      }
      setClassSessionsSort(newSort);
    },
    [classSessionsSort],
  );

  const handlePrivateLessonsSort = useCallback(
    (id: string, desc: boolean) => {
      const dir: "asc" | "desc" = desc ? "desc" : "asc";
      const newSort: Array<{ field: string; dir: "asc" | "desc" }> = [
        { field: id, dir },
      ];
      const currentField = privateLessonsSort?.[0]?.field;
      const currentDir = privateLessonsSort?.[0]?.dir;
      if (currentField === id && currentDir === dir) {
        return; // No change, prevent infinite loop
      }
      setPrivateLessonsSort(newSort);
    },
    [privateLessonsSort],
  );

  const handleCheckInsSort = useCallback(
    (id: string, desc: boolean) => {
      const dir: "asc" | "desc" = desc ? "desc" : "asc";
      const newSort: Array<{ field: string; dir: "asc" | "desc" }> = [
        { field: id, dir },
      ];
      const currentField = checkInsSort?.[0]?.field;
      const currentDir = checkInsSort?.[0]?.dir;
      if (currentField === id && currentDir === dir) {
        return; // No change, prevent infinite loop
      }
      setCheckInsSort(newSort);
    },
    [checkInsSort],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄູສອນ", to: "/app/master/instructors" },
            { label: "ໂປຣໄຟລ໌" },
          ]}
        />
      </Header>

      <Main>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດຄູສອນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!instructor ? null : (
            <div className="space-y-6">
              {/* Toolbar */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <InstructorAvatar
                    name={instructor.name}
                    className="size-14 text-xl"
                  />
                  <div className="space-y-2">
                    <h1 className="font-bold text-3xl tracking-tight">
                      {instructor.name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2">
                      <EmploymentTypeBadge type={instructor.employmentType} />
                      <InstructorStatusBadge status={instructor.status} />
                      <MonthlyFreeQuotaBadge
                        quota={instructor.monthlyFreeGeneralClassSessions}
                      />
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        nav({
                          to: "/app/master/instructors/$id/edit",
                          params: { id: instructor.id },
                        })
                      }
                    >
                      <EditIcon className="size-4" />
                      ແກ້ໄຂຄູສອນ
                    </Button>
                  </div>
                )}
              </div>

              {/* Tabs Navigation */}
              <Tabs
                value={activeTab}
                onValueChange={(value) =>
                  nav({
                    search: { ...search, tab: value as typeof activeTab },
                  })
                }
                className="w-full"
              >
                <div className="overflow-x-auto">
                  <TabsList className="h-auto w-full min-w-max border-b bg-transparent p-0 sm:w-full">
                    <TabsTrigger
                      value="general"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ຂໍ້ມູນທົ່ວໄປ
                    </TabsTrigger>
                    <TabsTrigger
                      value="class-sessions"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      ຊົ່ວໂມງຮຽນຄລາດສ໌
                    </TabsTrigger>
                    <TabsTrigger
                      value="private-lessons"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      Private Lessons
                    </TabsTrigger>
                    <TabsTrigger
                      value="check-ins"
                      className="whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                    >
                      Check-in History
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* General Info Tab */}
                {activeTab === "general" && (
                  <div className="mt-6 space-y-6">
                    {/* Summary Cards */}
                    <InstructorSummaryCards
                      summary={summary}
                      isLoading={!summary}
                    />

                    {/* Instructor Details */}
                    <Card>
                      <CardHeader>
                        <CardTitle>ຂໍ້ມູນຄູສອນ</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        {(instructor.monthlyFreeGeneralClassSessions ?? 0) > 0 ? (
                          <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-4">
                            <p className="mb-1 font-medium text-emerald-900 text-sm dark:text-emerald-100">
                              Retainer quota — session ຄລາສທົ່ວໄປຟຣີ
                            </p>
                            <MonthlyFreeQuotaBadge
                              quota={instructor.monthlyFreeGeneralClassSessions}
                            />
                            <p className="mt-2 text-muted-foreground text-xs">
                              Lead general class {instructor.monthlyFreeGeneralClassSessions}{" "}
                              ຄັ້ງກ່ອນຄິດເງິນເພີ່ມຕໍ່ເດືອນ
                            </p>
                          </div>
                        ) : null}

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <InfoField
                            label="ຊື່"
                            icon={<UserIcon className="size-4" />}
                          >
                            {instructor.name}
                          </InfoField>

                          <InfoField
                            label="ປະເພດການຈ້າງງານ"
                            icon={<UserCogIcon className="size-4" />}
                          >
                            <EmploymentTypeBadge type={instructor.employmentType} />
                          </InfoField>

                          {instructor.phoneNumber ? (
                            <InfoField
                              label="ເບີໂທ"
                              icon={<PhoneIcon className="size-4" />}
                            >
                              <span className="tabular-nums">
                                {instructor.phoneNumber}
                              </span>
                            </InfoField>
                          ) : null}

                          {instructor.email ? (
                            <InfoField
                              label="ອີເມວ"
                              icon={<MailIcon className="size-4" />}
                            >
                              {instructor.email}
                            </InfoField>
                          ) : null}

                          {instructor.bankAccountNumber ? (
                            <InfoField
                              label="ເລກບັນຊີທະນາຄານ"
                              icon={<LandmarkIcon className="size-4" />}
                            >
                              <span className="tabular-nums">
                                {instructor.bankAccountNumber}
                              </span>
                            </InfoField>
                          ) : null}

                          <InfoField
                            label="Session ຄລາສທົ່ວໄປຟຣີຕໍ່ເດືອນ"
                            icon={<UserCogIcon className="size-4" />}
                          >
                            <MonthlyFreeQuotaBadge
                              quota={instructor.monthlyFreeGeneralClassSessions ?? 0}
                            />
                          </InfoField>

                          <InfoField
                            label="ສະຖານະ"
                            icon={<PowerIcon className="size-4" />}
                          >
                            <InstructorStatusBadge status={instructor.status} />
                          </InfoField>
                        </div>

                        <div className="border-t pt-4">
                          <p className="mb-3 font-medium text-sm">
                            ອັດຕາ / ຈຳນວນເງິນ (ກີບ)
                          </p>
                          <PayoutRateGrid rates={instructor.payoutRates} />
                          <p className="mt-3 text-muted-foreground text-xs">
                            Private Package ທີ່ສະຕູດິໂອ:{" "}
                            <span className="text-foreground">
                              {instructor.privatePackagePayoutMode ===
                              "instructor_rate"
                                ? "ເຣດສ່ວນຕົວຂອງຄູຄົນນີ້"
                                : "ເຣດຕາຍຕົວຕາມຈຳນວນຄົນ (1–2: 150,000 · 3–4: 300,000 · 5–10: 350,000)"}
                            </span>
                          </p>
                        </div>

                        {instructor.notes ? (
                          <div className="rounded-lg border border-dashed bg-muted/20 p-4">
                            <p className="text-muted-foreground text-xs">ໝາຍເຫດ</p>
                            <p className="mt-1 whitespace-pre-wrap text-sm">
                              {instructor.notes}
                            </p>
                          </div>
                        ) : null}

                        <div className="border-t pt-4 text-muted-foreground text-xs">
                          <p>ສ້າງເມື່ອ: {formatDate(instructor.createdAt)}</p>
                          <p className="mt-1">
                            ອັບເດດລ່າສຸດ: {formatDate(instructor.updatedAt)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* ຊົ່ວໂມງຮຽນຄລາດສ໌ Tab */}
                {activeTab === "class-sessions" && (
                  <div className="mt-6">
                    <InstructorClassSessionsTable
                      data={classSessionsQuery.data?.data ?? []}
                      isLoading={classSessionsQuery.isLoading}
                      offset={classSessionsOffset}
                      limit={classSessionsLimit}
                      totalCount={classSessionsQuery.data?.meta.total ?? 0}
                      onPaginationChange={(offset) =>
                        setClassSessionsOffset(offset)
                      }
                      sortBy={
                        classSessionsSort
                          ? classSessionsSort[0]?.field
                          : undefined
                      }
                      sortOrder={
                        classSessionsSort
                          ? (classSessionsSort[0]?.dir as "asc" | "desc")
                          : undefined
                      }
                      onSortingChange={handleClassSessionsSort}
                    />
                  </div>
                )}

                {/* Private Lessons Tab */}
                {activeTab === "private-lessons" && (
                  <div className="mt-6">
                    <InstructorPrivateLessonsTable
                      data={privateLessonsQuery.data?.data ?? []}
                      isLoading={privateLessonsQuery.isLoading}
                      offset={privateLessonsOffset}
                      limit={privateLessonsLimit}
                      totalCount={privateLessonsQuery.data?.meta.total ?? 0}
                      onPaginationChange={(offset) =>
                        setPrivateLessonsOffset(offset)
                      }
                      sortBy={
                        privateLessonsSort
                          ? privateLessonsSort[0]?.field
                          : undefined
                      }
                      sortOrder={
                        privateLessonsSort
                          ? (privateLessonsSort[0]?.dir as "asc" | "desc")
                          : undefined
                      }
                      onSortingChange={handlePrivateLessonsSort}
                    />
                  </div>
                )}

                {/* Check-ins Tab */}
                {activeTab === "check-ins" && (
                  <div className="mt-6">
                    <InstructorCheckInsTable
                      data={checkInsQuery.data?.data ?? []}
                      isLoading={checkInsQuery.isLoading}
                      offset={checkInsOffset}
                      limit={checkInsLimit}
                      totalCount={checkInsQuery.data?.meta.total ?? 0}
                      onPaginationChange={(offset) => setCheckInsOffset(offset)}
                      sortBy={checkInsSort ? checkInsSort[0]?.field : undefined}
                      sortOrder={
                        checkInsSort
                          ? (checkInsSort[0]?.dir as "asc" | "desc")
                          : undefined
                      }
                      onSortingChange={handleCheckInsSort}
                    />
                  </div>
                )}
              </Tabs>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}

export default InstructorProfilePage;
